import { ChevronRight, FileSpreadsheet } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SubdomainGroup } from "@/components/SubdomainGroup";
import { FlatTableFallback } from "@/components/FlatTableFallback";
import type { TesterMapping } from "@/hooks/useWizardPersistence";
import type { TesterUser } from "@/services/testers";

interface FileMappingGroupProps {
  scenarioFile: string;
  fileMappings: TesterMapping[];
  isExpanded: boolean;
  onToggle: (file: string, open: boolean) => void;
  hasSubdomainData: boolean;
  expandedSubdomains: Set<string>;
  onSubdomainToggle: (key: string, open: boolean) => void;
  cognitoUsers: TesterUser[];
  isTestersLoading: boolean;
  isTestersError: boolean;
  onResolve: (mappingId: string, personIndex: number, user: TesterUser) => void;
  onRemove: (mappingId: string, personIndex: number) => void;
  onAddUser: (mappingId: string, user: TesterUser) => void;
  onRetryTesters: () => void;
  mappingSearch: string;
  scenarioStepKeys: Set<string>;
}

export function FileMappingGroup({
  scenarioFile,
  fileMappings,
  isExpanded,
  onToggle,
  hasSubdomainData,
  expandedSubdomains,
  onSubdomainToggle,
  cognitoUsers,
  isTestersLoading,
  isTestersError,
  onResolve,
  onRemove,
  onAddUser,
  onRetryTesters,
  mappingSearch,
  scenarioStepKeys,
}: FileMappingGroupProps) {
  const fileResolved = fileMappings.reduce(
    (n, m) => n + m.testPersons.filter(p => p.cognitoUserId).length, 0
  );
  const fileTotal = fileMappings.reduce((n, m) => n + m.testPersons.length, 0);
  const allFileResolved = fileTotal > 0 && fileResolved === fileTotal;

  const renderContent = () => {
    if (fileMappings.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-3 px-2 italic">
          {mappingSearch.trim()
            ? `No mappings in this file match "${mappingSearch}"`
            : "No tester roles found for this file"}
        </p>
      );
    }

    if (hasSubdomainData) {
      const groups = Array.from(
        fileMappings.reduce((groups, m) => {
          const key = m.subdomain || m.tester;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key)!.push(m);
          return groups;
        }, new Map<string, TesterMapping[]>())
      );

      return (
        <div className="space-y-1.5">
          {groups.map(([subdomainKey, mappingsInGroup]) => (
            <SubdomainGroup
              key={subdomainKey}
              subdomainKey={subdomainKey}
              groupDomain={mappingsInGroup[0]?.domain}
              mappingsInGroup={mappingsInGroup}
              isExpanded={expandedSubdomains.has(subdomainKey)}
              onToggle={onSubdomainToggle}
              cognitoUsers={cognitoUsers}
              isTestersLoading={isTestersLoading}
              isTestersError={isTestersError}
              onResolve={onResolve}
              onRemove={onRemove}
              onAddUser={onAddUser}
              onRetryTesters={onRetryTesters}
            />
          ))}
        </div>
      );
    }

    return (
      <FlatTableFallback
        mappings={fileMappings}
        scenarioStepKeys={scenarioStepKeys}
        cognitoUsers={cognitoUsers}
        isTestersLoading={isTestersLoading}
        isTestersError={isTestersError}
        onResolve={onResolve}
        onRemove={onRemove}
        onAddUser={onAddUser}
        onRetryTesters={onRetryTesters}
      />
    );
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={(open) => onToggle(scenarioFile, open)}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-background border border-border hover:bg-muted/30 transition-colors">
          <ChevronRight className={cn(
            "h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-150",
            isExpanded && "rotate-90"
          )} />
          <FileSpreadsheet className="h-4 w-4 text-primary shrink-0" />
          <span className="font-semibold flex-1 text-left text-sm truncate">{scenarioFile}</span>
          <span className={cn(
            "text-xs tabular-nums shrink-0",
            allFileResolved
              ? "text-success"
              : fileResolved > 0
              ? "text-amber-600"
              : "text-muted-foreground"
          )}>
            {fileResolved}/{fileTotal}
          </span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 ml-3 pl-3 border-l border-muted space-y-1">
          {renderContent()}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
